"use client"

import { useCallback, useEffect, useId, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { AlertCircle, Check, Loader2, X } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  groupHistoryEntriesByDate,
  replayHistory,
} from "@/lib/crdt/crdt-history"
import type { HistoryTimelineDateGroup, HistoryTimelineEntry } from "@/lib/crdt/types"
import { useThemeMode } from "@/lib/themes/theme-mode"
import { editorThemeVars } from "@/lib/themes/editor-theme-vars"

type LoadPhase = "idle" | "loading" | "success" | "error"

function formatEntryClock(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(parsed)
}

function defaultActiveId(groups: HistoryTimelineDateGroup[]): string | null {
  return groups[0]?.entries[0]?.id ?? null
}

export type VersionHistoryPanelProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  triggerRef: React.RefObject<HTMLButtonElement | null>
  historyUrl?: string
  sessionId?: string
  /** Baseline files at session connect (template / reconstruct) for incremental replay. */
  replaySeedFiles?: { path: string; content: string }[]
  mainFilePath?: string
  projectId?: string
  onSelectEntry?: (entry: HistoryTimelineEntry) => void
}

export function VersionHistoryPanel({
  open,
  onOpenChange,
  triggerRef,
  historyUrl,
  sessionId,
  replaySeedFiles,
  mainFilePath,
  projectId,
  onSelectEntry,
}: VersionHistoryPanelProps) {
  const panelTitleId = useId()
  const closeRef = useRef<HTMLButtonElement>(null)
  const { theme } = useThemeMode()
  const [mounted, setMounted] = useState(false)
  const [loadPhase, setLoadPhase] = useState<LoadPhase>("idle")
  const [loadError, setLoadError] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null)
  const [groups, setGroups] = useState<HistoryTimelineDateGroup[]>([])
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null)
  const [usedClientFallback, setUsedClientFallback] = useState(false)

  const activeEntry =
    groups.flatMap((g) => g.entries).find((e) => e.id === activeEntryId) ?? null

  useEffect(() => {
    setMounted(true)
  }, [])

  const close = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => {
      closeRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        close()
      }
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [open, close])

  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (wasOpenRef.current && !open) {
      triggerRef.current?.focus()
    }
    wasOpenRef.current = open
  }, [open, triggerRef])

  useEffect(() => {
    if (!open) {
      setLoadPhase("idle")
      setLoadError(null)
      setProgress(null)
      setUsedClientFallback(false)
      return
    }

    console.log("[HISTORY:PANEL] opened", {
      session_id: sessionId ?? null,
      historyUrl: historyUrl ?? null,
      replaySeedFileCount: replaySeedFiles?.length ?? 0,
      mainFilePath: mainFilePath ?? null,
      request: historyUrl
        ? { via: "replayHistory", limit: 50, historyUrl }
        : { via: "none", reason: "missing historyUrl" },
    })

    if (!historyUrl) {
      setLoadPhase("error")
      setLoadError(
        sessionId
          ? `Version history is unavailable for session ${sessionId}.`
          : "Version history is unavailable for this session.",
      )
      setGroups([])
      setActiveEntryId(null)
      return
    }

    let cancelled = false

    const run = async () => {
      setLoadPhase("loading")
      setLoadError(null)
      setProgress({ current: 0, total: 0 })
      setGroups([])
      setActiveEntryId(null)

      try {
        const result = await replayHistory(historyUrl, {
          limit: 100,
          seedFiles: replaySeedFiles,
          preferredPath: mainFilePath,
          projectId,
          onProgress: (current, total) => {
            if (!cancelled) setProgress({ current, total })
          },
        })

        if (cancelled) return

        setUsedClientFallback(Boolean(result.usedClientFallback))
        const grouped = groupHistoryEntriesByDate(result.entries)
        const snapshotCount = grouped.reduce((n, g) => n + g.entries.length, 0)
        console.log("[HISTORY:PANEL] render snapshots", {
          session_id: result.sessionId,
          snapshotCount,
          dateGroupCount: grouped.length,
          usedClientFallback: Boolean(result.usedClientFallback),
          latestVersion: result.latestVersion,
          stats: result.stats,
        })
        setGroups(grouped)
        setActiveEntryId(defaultActiveId(grouped))
        setLoadPhase("success")
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : "Failed to load version history"
        console.log("[HISTORY:PANEL] load error", {
          session_id: sessionId ?? null,
          historyUrl,
          error: message,
        })
        setLoadPhase("error")
        setLoadError(message)
        setGroups([])
      } finally {
        if (!cancelled) setProgress(null)
      }
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [open, historyUrl, sessionId, replaySeedFiles, mainFilePath, projectId])

  if (!mounted) return null

  const progressLabel =
    progress && progress.total > 0
      ? `Replaying version ${progress.current} of ${progress.total}…`
      : "Loading version history…"

  const portal = (
    <>
      <HistoryBackdrop open={open} onClose={close} />

      <aside
        id="version-history-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={panelTitleId}
        aria-busy={loadPhase === "loading"}
        inert={!open}
        className={cn(
          "fixed right-0 top-0 z-[1000] flex h-screen w-full max-w-full flex-col bg-[var(--ed-vh-panel-bg)] transition-transform duration-[250ms] ease-in-out sm:w-[360px] sm:max-w-[360px]",
          open ? "translate-x-0" : "translate-x-full pointer-events-none",
        )}
        style={{ ...editorThemeVars(theme), boxShadow: "var(--ed-vh-panel-shadow)" }}
      >
        <header className="flex shrink-0 items-center justify-between border-b border-[var(--ed-vh-header-border)] px-4 py-5">
          <h2
            id={panelTitleId}
            className="m-0 text-base font-semibold text-[var(--ed-vh-title-text)]"
          >
            Version History
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-sm text-[var(--ed-vh-close-text)] outline-none transition-colors hover:bg-[var(--ed-vh-close-hover-bg)] focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2"
            aria-label="Close version history"
            onClick={close}
          >
            <X className="size-4" aria-hidden />
          </button>
        </header>

        {loadPhase === "loading" ? (
          <div className="flex items-center gap-2 border-b border-[var(--ed-vh-loading-border)] px-4 py-3 text-sm text-[var(--ed-vh-subtext)]">
            <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            <span>{progressLabel}</span>
          </div>
        ) : null}

        {loadPhase === "error" ? (
          <div
            className="mx-4 mt-4 flex gap-2 rounded-md border border-[var(--ed-vh-offline-border)] bg-[var(--ed-vh-offline-bg)] px-3 py-2 text-sm text-[var(--ed-vh-offline-text)]"
            role="alert"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <p className="m-0">{loadError}</p>
          </div>
        ) : null}

        {loadPhase === "success" && usedClientFallback ? (
          <div
            className="mx-4 mt-4 flex gap-2 rounded-md border border-[var(--ed-vh-offline-border)] bg-[var(--ed-vh-offline-bg)] px-3 py-2 text-sm text-[var(--ed-vh-offline-text)]"
            role="status"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <p className="m-0">
              Server history returned empty placeholders (AAA=). Showing edits captured from this
              browser session instead. Backend must persist raw Yjs sync_update bytes.
            </p>
          </div>
        ) : null}

        <div
          className={cn(
            "history-panel-scroll flex-1 overflow-y-auto overscroll-contain px-4 py-4",
            "[scrollbar-width:thin]",
            "[scrollbar-color:var(--ed-vh-scrollbar-thumb)_transparent]",
            "[&::-webkit-scrollbar]:w-1.5",
            "[&::-webkit-scrollbar-track]:bg-transparent",
            "[&::-webkit-scrollbar-thumb]:rounded-full",
            "[&::-webkit-scrollbar-thumb]:bg-[var(--ed-vh-scrollbar-thumb)]",
          )}
        >
          {loadPhase === "success" && groups.length === 0 ? (
            <p className="m-0 px-1 text-sm text-[var(--ed-vh-empty-text)]">
              No edits recorded yet for this session.
            </p>
          ) : null}

          {groups.map((group, groupIndex) => (
            <section key={group.date} aria-label={`Versions on ${group.date}`}>
              <h3
                className={cn(
                  "border-b border-[var(--ed-vh-group-label-border)] pb-1 text-[13px] font-semibold text-[var(--ed-vh-group-label-text)]",
                  groupIndex === 0 ? "mt-0" : "mt-4",
                  "mb-3",
                )}
              >
                {group.date}
              </h3>
              <ul className="m-0 list-none space-y-3 p-0">
                {group.entries.map((entry) => {
                  const isActive = activeEntryId === entry.id
                  return (
                    <li key={entry.id}>
                      <button
                        type="button"
                        aria-current={isActive ? "true" : undefined}
                        className={cn(
                          "w-full rounded-md border border-transparent p-2 text-left transition-colors",
                          "hover:bg-[var(--ed-vh-item-hover-bg)]",
                          isActive &&
                            "border-[var(--ed-vh-item-selected-border)] bg-[var(--ed-vh-item-selected-bg)]",
                        )}
                        onClick={() => {
                          setActiveEntryId(entry.id)
                          onSelectEntry?.(entry)
                        }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-[var(--ed-vh-item-time-text)]">
                            {formatEntryClock(entry.at)}
                            {entry.userName ? ` · ${entry.userName}` : ""}
                          </span>
                          <div className="flex min-w-0 items-center gap-1.5">
                            <span className="truncate text-xs font-medium text-[var(--ed-vh-item-name-text)]">
                              v{entry.version}
                            </span>
                            {isActive ? (
                              <Check
                                className="size-3.5 shrink-0 text-[var(--ed-vh-icon-accent)]"
                                aria-hidden
                              />
                            ) : null}
                          </div>
                        </div>
                        <p className="mt-1.5 text-[13px] leading-snug text-[var(--ed-vh-item-desc-text)]">
                          {entry.description}
                        </p>
                        <p className="mt-1 truncate font-mono text-[11px] text-neutral-500 dark:text-neutral-500">
                          {entry.path}
                        </p>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>

        {activeEntry && loadPhase === "success" ? (
          <section
            className="shrink-0 border-t border-[var(--ed-vh-footer-border)] bg-[var(--ed-vh-footer-bg)] px-4 py-3"
            aria-label={`Diff for version ${activeEntry.version}`}
          >
            <h4 className="m-0 text-xs font-semibold uppercase tracking-wide text-[var(--ed-vh-footer-label)]">
              v{activeEntry.version} · {activeEntry.path}
            </h4>
            <div className="mt-2 grid max-h-40 grid-cols-1 gap-2 overflow-hidden sm:grid-cols-2">
              <DiffBlock label="Before" text={activeEntry.before} />
              <DiffBlock label="After" text={activeEntry.after} />
            </div>
          </section>
        ) : null}
      </aside>
    </>
  )

  return createPortal(portal, document.body)
}

function HistoryBackdrop({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <div
      aria-hidden={!open}
      className={cn(
        "fixed inset-0 z-[999] bg-black/20 transition-opacity duration-200 ease-out",
        open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
      )}
      onClick={onClose}
    />
  )
}

function DiffBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className="min-h-0">
      <p className="m-0 mb-1 text-[10px] font-semibold uppercase text-[var(--ed-vh-footer-label)]">
        {label}
      </p>
      <pre
        className="m-0 max-h-28 overflow-auto rounded border border-[var(--ed-vh-code-border)] bg-[var(--ed-vh-code-bg)] p-2 font-mono text-[10px] leading-relaxed text-[var(--ed-vh-code-text)]"
        aria-label={`${label} content`}
      >
        {text || "(empty)"}
      </pre>
        </div>
  )
}
